import React, { useCallback, useEffect, useState } from "react";
import {
  FaCheckCircle,
  FaClock,
  FaCopy,
  FaExclamationTriangle,
  FaFacebook,
  FaGoogle,
  FaInstagram,
  FaKey,
  FaPause,
  FaPlay,
  FaRedo,
  FaSync,
  FaTrash,
  FaWhatsapp,
} from "react-icons/fa";
import { useConfirm, useNotify } from "../../components/ConfirmModal";
import VaultPanel from "../../components/vault/VaultPanel";
import { Empty } from "./WorkUi";
import { errorMessage, fmtShortDate } from "./workApi";

function CopyField({ label, value }) {
  const notify = useNotify();
  if (!value) return null;
  return (
    <label className="wk-field">
      {label}
      <span className="wk-quick-add" style={{ marginTop: 0 }}>
        <input readOnly value={value} onFocus={(e) => e.target.select()} />
        <button
          type="button"
          className="dmp-btn dmp-btn--ghost"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              notify.success("Copied");
            } catch {
              notify.error("Select the text and copy it manually.");
            }
          }}
          aria-label={`Copy ${label}`}
        >
          <FaCopy aria-hidden="true" />
        </button>
      </span>
    </label>
  );
}

const sourceText = (source) => (source === "vault" ? "Your own app (from Vault)" : "Tumma's app");

const HEALTH = {
  ok: { label: "Working", tone: "green", icon: FaCheckCircle },
  expiring: { label: "Sign in again soon", tone: "amber", icon: FaClock },
  expired: { label: "Reconnect needed", tone: "red", icon: FaExclamationTriangle },
  error: { label: "Last check failed", tone: "amber", icon: FaExclamationTriangle },
};

/** Health of a sign-in with words + icon (never colour alone), its end date and last check. */
export function HealthPill({ health, expiresOn, checkedOn, error, refreshable }) {
  const info = HEALTH[health] || HEALTH.ok;
  const Icon = info.icon;
  return (
    <div>
      <span className={`wk-pill wk-pill--${info.tone} wk-pill--sm`}>
        <Icon aria-hidden="true" className="wk-pill__icon" />
        {info.label}
      </span>
      <div className="wk-muted" style={{ fontSize: 12 }}>
        {expiresOn && !refreshable ? `Sign-in ends ${fmtShortDate(expiresOn)}` : refreshable ? "Renews automatically" : "No end date"}
        {checkedOn ? ` · checked ${fmtShortDate(checkedOn)}` : ""}
      </div>
      {error ? <div style={{ color: "#b91c1c", fontSize: 12 }}>{error}</div> : null}
    </div>
  );
}

/** Check + Reconnect buttons for one sign-in. */
export function SignInActions({ onCheck, onReconnect, reconnectLabel = "Reconnect", needsReconnect, busy }) {
  return (
    <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
      <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" onClick={onCheck} disabled={busy}>
        <FaSync aria-hidden="true" /> {busy ? "Checking…" : "Check"}
      </button>
      {onReconnect ? (
        <button type="button" className={`dmp-btn dmp-btn--sm${needsReconnect ? "" : " dmp-btn--ghost"}`} onClick={onReconnect}>
          <FaRedo aria-hidden="true" /> {reconnectLabel}
        </button>
      ) : null}
    </span>
  );
}

/** Which app credentials each platform uses, and the URLs to register in your own apps. */
export function CredentialsCard({ data }) {
  const creds = data?.credentials;
  const urls = data?.urls;
  if (!creds) return null;
  return (
    <div className="dmp-card">
      <h3 className="dmp-card__title">Whose app is used</h3>
      <dl className="wk-kv">
        <dt>Facebook & Instagram</dt>
        <dd>
          {sourceText(creds.meta.source)}
          {!creds.meta.ready ? <span className="wk-count wk-count--amber" style={{ marginLeft: 6 }}>Not set up</span> : null}
        </dd>
        <dt>Google / YouTube</dt>
        <dd>
          {sourceText(creds.google.source)}
          {!creds.google.developer_token ? <span className="wk-muted"> · no Google Ads developer token</span> : null}
        </dd>
        <dt>LinkedIn</dt>
        <dd>{sourceText(creds.linkedin.source)}</dd>
      </dl>
      <p className="wk-muted" style={{ fontSize: 13 }}>
        To use your own developer apps, add them in Settings &gt; Vault (type API, pick the Meta / Google Ads / LinkedIn
        template). Then register these URLs in your app:
      </p>
      {urls ? (
        <>
          <CopyField label="Meta redirect URL (pages)" value={urls.oauth_redirects?.meta} />
          <CopyField label="Meta redirect URL (ad accounts)" value={urls.oauth_redirects?.metaads} />
          <CopyField label="Meta redirect URL (WhatsApp)" value={urls.oauth_redirects?.whatsapp} />
          <CopyField label="Google redirect URL (YouTube)" value={urls.oauth_redirects?.youtube} />
          <CopyField label="Google redirect URL (Google Ads)" value={urls.oauth_redirects?.googleads} />
          <CopyField label="LinkedIn redirect URL" value={urls.oauth_redirects?.linkedin} />
          <CopyField
            label={creds.meta.source === "vault" ? "Meta webhook URL (your app)" : "Meta webhook URL"}
            value={creds.meta.source === "vault" ? urls.meta_webhook_own_app : urls.meta_webhook}
          />
          {creds.meta.source === "vault" ? (
            <p className="wk-muted" style={{ fontSize: 12 }}>
              In your Meta app, subscribe Page fields feed, leadgen, messages, messaging_postbacks and message_echoes,
              the Instagram fields comments and messages, and the WhatsApp Business Account field messages, with the
              Webhook Verify Token from your Vault item.
            </p>
          ) : null}
        </>
      ) : (
        <p className="wk-muted">The server's public address (SOCIAL_OAUTH_REDIRECT_BASE) is not set yet.</p>
      )}
    </div>
  );
}

/** Meta ad accounts and Google Ads accounts whose spend, clicks and leads are synced. */
/** ``setupOnly``: connect / remove accounts only (no sync, pause or last-sync details). */
/** ``syncOnly``: sync, pause and last-sync details only (connecting and removing are in API Connections). */
export function AdAccountsCard({ api, tenantId, providers, onConnect, setupOnly = false, syncOnly = false }) {
  const notify = useNotify();
  const confirm = useConfirm();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const params = api.isStaff ? { tenant_id: tenantId } : undefined;

  const load = useCallback(async () => {
    try {
      setRows((await api.adAccounts(params))?.accounts || []);
    } catch (err) {
      notify.error(errorMessage(err, "Could not load ad accounts"));
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const sync = async () => {
    setBusy(true);
    try {
      const res = await api.syncAds(params || {});
      notify.success(
        `Synced ${res.accounts} account(s): ${res.stored} ad-day rows${res.unlinked ? `, ${res.unlinked} not linked to a DM campaign yet` : ""}`
      );
      load();
    } catch (err) {
      notify.error(errorMessage(err, "Could not sync"));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (acc, Status) => {
    try {
      await api.updateAdAccount(acc.Ad_Account_Id, { Status });
      load();
    } catch (err) {
      notify.error(errorMessage(err));
    }
  };

  const remove = async (acc) => {
    if (!(await confirm({ title: "Remove ad account", message: `Stop syncing ${acc.Account_Name}?` }))) return;
    try {
      await api.deleteAdAccount(acc.Ad_Account_Id);
      load();
    } catch (err) {
      notify.error(errorMessage(err));
    }
  };

  return (
    <div className="dmp-card">
      <h3 className="dmp-card__title">
        Ad accounts
        {rows?.length && !setupOnly ? (
          <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" onClick={sync} disabled={busy}>
            <FaSync aria-hidden="true" /> {busy ? "Syncing…" : "Sync now"}
          </button>
        ) : null}
      </h3>
      {setupOnly ? null : (
        <p className="wk-muted" style={{ marginTop: -6 }}>
          Spend, impressions, clicks and leads sync every 6 hours into each DM campaign's Responses. Link a DM campaign to
          its Meta / Google campaign in the campaign's edit form.
        </p>
      )}
      {syncOnly ? null : (
        <div className="wk-actions-bar">
          <button type="button" className="dmp-btn" disabled={!providers?.metaads} onClick={() => onConnect("metaads")}>
            <FaFacebook aria-hidden="true" /> Connect Meta ad accounts
          </button>
          <button type="button" className="dmp-btn" disabled={!providers?.googleads} onClick={() => onConnect("googleads")}>
            <FaGoogle aria-hidden="true" /> Connect Google Ads
          </button>
        </div>
      )}
      {rows === null ? (
        <div className="wk-muted">Loading…</div>
      ) : rows.length ? (
        <table className="wk-table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Credentials</th>
              {setupOnly ? null : <th>Last sync</th>}
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.Ad_Account_Id}>
                <td>
                  <strong>{a.Platform}</strong> · {a.Account_Name}
                  <div className="wk-muted" style={{ fontSize: 12 }}>
                    {a.External_Account_Id}
                    {a.Currency ? ` · ${a.Currency}` : ""}
                  </div>
                  {setupOnly && a.Last_Error ? <div style={{ color: "#b91c1c", fontSize: 12 }}>{a.Last_Error}</div> : null}
                </td>
                <td>{a.Source === "vault" ? "Your app (Vault)" : "Your sign-in"}</td>
                {setupOnly ? null : (
                  <td>
                    {a.Status === "Paused" ? "Paused" : a.Last_Sync_On ? fmtShortDate(a.Last_Sync_On) : "Not yet"}
                    {a.Last_Error ? <div style={{ color: "#b91c1c", fontSize: 12 }}>{a.Last_Error}</div> : null}
                  </td>
                )}
                <td style={{ whiteSpace: "nowrap" }}>
                  {setupOnly ? null : a.Status === "Paused" ? (
                    <button type="button" className="dmp-link" onClick={() => setStatus(a, "Active")} aria-label="Resume sync">
                      <FaPlay aria-hidden="true" />
                    </button>
                  ) : (
                    <button type="button" className="dmp-link" onClick={() => setStatus(a, "Paused")} aria-label="Pause sync">
                      <FaPause aria-hidden="true" />
                    </button>
                  )}
                  {syncOnly ? null : (
                    <>
                      {" "}
                      <button type="button" className="dmp-link" onClick={() => remove(a)} aria-label={`Remove ${a.Account_Name}`}>
                        <FaTrash aria-hidden="true" />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <Empty icon={FaKey} title="No ad accounts yet">
          {syncOnly
            ? "Connect them in Social Connections > API Connections."
            : "Sign in with the business account that runs your ads, or add your own app with its token and account ids in Vault."}
        </Empty>
      )}
    </div>
  );
}

/**
 * The client's WhatsApp Business numbers (inbox, broadcasts, Click-to-WhatsApp leads).
 * ``syncOnly``: status and Check only (connecting, removing and the webhook URL are in API Connections).
 */
const numberSourceText = (source, partnerName) =>
  ({ vault: "Your app (Vault)", partner: `Managed by ${partnerName || "Digix Leads"}`, token: "Token" })[source] ||
  "Your sign-in";

export function WhatsAppNumbersCard({ api, tenantId, providers, urls, metaSource, partnerName, onConnect, syncOnly = false }) {
  const notify = useNotify();
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const params = api.isStaff ? { tenant_id: tenantId } : undefined;

  const load = useCallback(async () => {
    try {
      setData(await api.waNumbers(params));
    } catch (err) {
      notify.error(errorMessage(err, "Could not load WhatsApp numbers"));
      setData({ numbers: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const [checking, setChecking] = useState(null);
  const check = async (n) => {
    setChecking(n.Number_Id);
    try {
      const res = await api.checkWaNumber(n.Number_Id);
      (res.Health === "ok" ? notify.success : notify.error)(
        res.Health === "ok" ? `${res.Display_Number || res.Phone_Number_Id} is working` : res.Last_Error || "The number needs attention"
      );
      load();
    } catch (err) {
      notify.error(errorMessage(err, "Could not check the number"));
    } finally {
      setChecking(null);
    }
  };

  const remove = async (n) => {
    if (!(await confirm({ title: "Remove number", message: `Stop using ${n.Display_Number || n.Phone_Number_Id} in Cloud Office?` }))) return;
    try {
      await api.deleteWaNumber(n.Number_Id);
      load();
    } catch (err) {
      notify.error(errorMessage(err));
    }
  };

  return (
    <div className="dmp-card">
      <h3 className="dmp-card__title">
        <span>
          <FaWhatsapp aria-hidden="true" /> WhatsApp
        </span>
      </h3>
      <p className="wk-muted" style={{ marginTop: -6 }}>
        Customers' messages land in DMS &gt; WhatsApp; new numbers become leads (with the ad, for Click-to-WhatsApp ads).
        Use the client's own WhatsApp Business number, not a personal one.
      </p>
      {syncOnly ? null : (
        <div className="wk-actions-bar">
          <button type="button" className="dmp-btn" disabled={!providers?.whatsapp} onClick={() => onConnect("whatsapp")}>
            <FaWhatsapp aria-hidden="true" /> Connect WhatsApp Business
          </button>
        </div>
      )}
      {data === null ? (
        <div className="wk-muted">Loading…</div>
      ) : data.numbers.length ? (
        <table className="wk-table">
          <thead>
            <tr>
              <th>Number</th>
              <th>Credentials</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.numbers.map((n) => (
              <tr key={n.Number_Id}>
                <td>
                  <strong>{n.Display_Number || n.Phone_Number_Id}</strong>
                  {n.Verified_Name ? <div className="wk-muted" style={{ fontSize: 12 }}>{n.Verified_Name}</div> : null}
                </td>
                <td>{numberSourceText(n.Source, partnerName)}</td>
                <td>
                  <HealthPill health={n.Health} expiresOn={n.Token_Expires_On} checkedOn={n.Last_Checked_On} error={n.Last_Error} />
                  {n.Quality ? <div className="wk-muted" style={{ fontSize: 12 }}>Quality: {n.Quality}</div> : null}
                  {n.Health === "expired" && n.Source === "vault" ? (
                    <div className="wk-muted" style={{ fontSize: 12 }}>Update the Access Token in Vault, then press Check.</div>
                  ) : null}
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <SignInActions
                    busy={checking === n.Number_Id}
                    onCheck={() => check(n)}
                    onReconnect={n.Source === "oauth" && providers?.whatsapp && onConnect ? () => onConnect("whatsapp") : null}
                    needsReconnect={n.Health === "expired" || n.Health === "expiring"}
                  />
                  {syncOnly ? null : (
                    <>
                      {" "}
                      <button type="button" className="dmp-link" onClick={() => remove(n)} aria-label={`Remove ${n.Display_Number || n.Phone_Number_Id}`}>
                        <FaTrash aria-hidden="true" />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <Empty icon={FaWhatsapp} title="No WhatsApp number yet">
          {syncOnly
            ? "Connect it in Social Connections > API Connections."
            : "Sign in with the Meta business account that owns the number, or add a Vault API item (WhatsApp template) with the Phone Number ID, WABA ID and a permanent token."}
        </Empty>
      )}
      {urls && !syncOnly ? (
        <CopyField
          label={metaSource === "vault" ? "WhatsApp webhook URL (your app)" : "WhatsApp webhook URL"}
          value={metaSource === "vault" ? urls.whatsapp_webhook_own_app : urls.whatsapp_webhook}
        />
      ) : null}
    </div>
  );
}

/** Staff: Tumma's own apps and WhatsApp alert number, kept in Tumma's Vault (used when a client has none). */
export function TummaAppsCard({ api }) {
  const notify = useNotify();
  const [setup, setSetup] = useState(null);
  const [open, setOpen] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(() => {
    api
      .tummaSetup()
      .then(setSetup)
      .catch(() => setSetup(null));
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const testAlert = async () => {
    setTesting(true);
    try {
      const res = await api.testWaAlert();
      notify.success(`Test alert sent to ${res.to} with template ${res.template}. Check your WhatsApp.`);
    } catch (err) {
      notify.error(errorMessage(err, "Could not send the test alert"));
    } finally {
      setTesting(false);
    }
  };

  if (!setup) return null;
  const apps = setup.apps || {};
  const row = (label, ok, hint) => (
    <>
      <dt>{label}</dt>
      <dd>
        {ok ? (
          <span className="wk-pill wk-pill--green wk-pill--sm">
            <FaCheckCircle aria-hidden="true" className="wk-pill__icon" />
            Set up
          </span>
        ) : (
          <span className="wk-pill wk-pill--grey wk-pill--sm">Not set up</span>
        )}
        {hint ? <span className="wk-muted"> · {hint}</span> : null}
      </dd>
    </>
  );
  const urls = setup.urls;
  return (
    <div className="dmp-card">
      <h3 className="dmp-card__title">
        Tumma's apps
        {setup.can_manage ? (
          <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            <FaKey aria-hidden="true" /> {open ? "Close" : "Manage"}
          </button>
        ) : null}
      </h3>
      <p className="wk-muted" style={{ marginTop: -6 }}>
        Used for clients that have no app of their own in their Vault: they sign in to their business accounts through
        these apps. Kept encrypted in Tumma's Vault, not in server files.
      </p>
      <dl className="wk-kv">
        {row("Meta app (Facebook, Instagram, ads, WhatsApp sign-in)", apps.meta, apps.meta && !apps.meta_webhook_token ? "add a Webhook Verify Token" : null)}
        {row("Google app (YouTube, Google Ads)", apps.google, apps.google && !apps.google_developer_token ? "no Google Ads developer token" : null)}
        {row("LinkedIn app", apps.linkedin)}
        {row("WhatsApp alert number", apps.whatsapp_alerts, `template ${apps.alert_template}`)}
        {row(
          `Partner access (${apps.partner_name || "Digix Leads"} system user)`,
          apps.partner,
          apps.partner && !apps.partner_business_id ? "add the Business ID to list WhatsApp accounts" : "System User Token in the Meta item"
        )}
      </dl>
      {apps.whatsapp_alerts && setup.can_manage ? (
        <div className="wk-actions-bar">
          <button type="button" className="dmp-btn dmp-btn--ghost" onClick={testAlert} disabled={testing}>
            <FaWhatsapp aria-hidden="true" /> {testing ? "Sending…" : "Send me a test alert"}
          </button>
        </div>
      ) : null}
      {urls ? (
        <>
          <CopyField label="Meta webhook URL (Tumma's app)" value={urls.meta_webhook} />
          <CopyField label="WhatsApp webhook URL (Tumma's app)" value={urls.whatsapp_webhook} />
        </>
      ) : null}
      {open ? (
        <div style={{ marginTop: 12 }}>
          <VaultPanel
            basePath="/org/vault"
            title="Tumma's apps"
            addLabel="Add app"
            description="API items only. Pick the Meta, Google Ads, LinkedIn or WhatsApp template; open with your own login password. Every access is logged."
            emptyText="No apps yet. Add Tumma's Meta / Google / LinkedIn app, and the WhatsApp number for staff alerts."
          />
          <button type="button" className="dmp-btn dmp-btn--ghost" style={{ marginTop: 8 }} onClick={load}>
            <FaSync aria-hidden="true" /> Refresh status
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Staff: Pages (with Instagram) and WhatsApp numbers clients shared with the partner business (Digix Leads),
 * assigned to the picked client. They then publish / reply / message with the partner's system user token.
 */
export function PartnerAssetsCard({ api, tenantId, partnerName = "Digix Leads", onAssigned }) {
  const notify = useNotify();
  const [assets, setAssets] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pages, setPages] = useState([]);
  const [numbers, setNumbers] = useState([]);
  const [saving, setSaving] = useState(false);
  const name = assets?.partner || partnerName;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setAssets(await api.metaPartnerAssets());
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load the shared accounts"));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    setPages([]);
    setNumbers([]);
  }, [tenantId]);

  const toggle = (setter, key) => setter((list) => (list.includes(key) ? list.filter((k) => k !== key) : [...list, key]));

  const assign = async () => {
    setSaving(true);
    try {
      const res = await api.assignMetaPartnerAssets({
        tenant_id: tenantId,
        Page_Ids: pages,
        Numbers: numbers.map((key) => {
          const [Waba_Id, Phone_Number_Id] = key.split(":");
          return { Waba_Id, Phone_Number_Id };
        }),
      });
      notify.success(`Assigned ${res.pages} Page(s), ${res.instagram} Instagram account(s) and ${res.numbers} WhatsApp number(s).`);
      (res.warnings || []).forEach((w) => notify.error(w));
      setPages([]);
      setNumbers([]);
      await load();
      if (onAssigned) onAssigned();
    } catch (err) {
      notify.error(errorMessage(err, "Could not assign"));
    } finally {
      setSaving(false);
    }
  };

  const owner = (assigned) => {
    if (!assigned) return <span className="wk-muted">Not assigned</span>;
    if (String(assigned.tenant_id) === String(tenantId)) {
      return (
        <span className="wk-pill wk-pill--green wk-pill--sm">
          <FaCheckCircle aria-hidden="true" className="wk-pill__icon" />
          This client
        </span>
      );
    }
    return <span className="wk-pill wk-pill--grey wk-pill--sm">{assigned.name}</span>;
  };

  const picked = pages.length + numbers.length;
  const waNumbers = (assets?.whatsapp || []).flatMap((w) => w.Numbers.map((n) => ({ ...n, waba: w })));

  return (
    <div className="dmp-card">
      <h3 className="dmp-card__title">
        <span>{name} partner access</span>
        <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" onClick={load} disabled={loading}>
          <FaSync aria-hidden="true" /> {loading ? "Loading…" : assets ? "Refresh" : "Show shared accounts"}
        </button>
      </h3>
      <p className="wk-muted" style={{ marginTop: -6 }}>
        The client adds {name} as a partner (Meta Business settings &gt; Partners) and shares its Facebook Page and
        WhatsApp Business account; {name} assigns them to its system user. Pick them here to manage them for this
        client. No client sign-in needed, and the access does not expire.
      </p>
      {error ? <div className="dmp-error">{error}</div> : null}
      {assets ? (
        <>
          {(assets.warnings || []).map((w) => (
            <div key={w} className="wk-muted" style={{ color: "#b45309", fontSize: 13 }}>
              <FaExclamationTriangle aria-hidden="true" /> {w}
            </div>
          ))}
          {assets.pages.length || waNumbers.length ? (
            <table className="wk-table">
              <thead>
                <tr>
                  <th aria-label="Pick" />
                  <th>Account</th>
                  <th>Assigned to</th>
                </tr>
              </thead>
              <tbody>
                {assets.pages.map((p) => {
                  const free = !p.Assigned_To;
                  return (
                    <tr key={`page-${p.Page_Id}`}>
                      <td>
                        <input
                          type="checkbox"
                          disabled={!free}
                          checked={pages.includes(p.Page_Id)}
                          onChange={() => toggle(setPages, p.Page_Id)}
                          aria-label={`Assign Page ${p.Name || p.Page_Id}`}
                        />
                      </td>
                      <td>
                        <FaFacebook aria-hidden="true" /> <strong>{p.Name || p.Page_Id}</strong>
                        {p.Instagram ? (
                          <div className="wk-muted" style={{ fontSize: 12 }}>
                            <FaInstagram aria-hidden="true" /> @{p.Instagram.Username || p.Instagram.Id} (comes with the Page)
                          </div>
                        ) : null}
                      </td>
                      <td>{owner(p.Assigned_To)}</td>
                    </tr>
                  );
                })}
                {waNumbers.map((n) => {
                  const key = `${n.waba.Waba_Id}:${n.Phone_Number_Id}`;
                  const free = !n.Assigned_To;
                  return (
                    <tr key={`wa-${key}`}>
                      <td>
                        <input
                          type="checkbox"
                          disabled={!free}
                          checked={numbers.includes(key)}
                          onChange={() => toggle(setNumbers, key)}
                          aria-label={`Assign WhatsApp number ${n.Display_Number || n.Phone_Number_Id}`}
                        />
                      </td>
                      <td>
                        <FaWhatsapp aria-hidden="true" /> <strong>{n.Display_Number || n.Phone_Number_Id}</strong>
                        <div className="wk-muted" style={{ fontSize: 12 }}>
                          {n.Verified_Name || "—"} · {n.waba.Name || n.waba.Waba_Id}
                          {n.waba.Shared_By === "partner" ? ` · ${name}'s own account` : ""}
                        </div>
                      </td>
                      <td>{owner(n.Assigned_To)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <Empty icon={FaFacebook} title={`Nothing is shared with ${name} yet`}>
              Ask the client to add {name} as a partner and share its Page and WhatsApp account, then assign them to the
              system user in {name}'s Business settings.
            </Empty>
          )}
          <div className="wk-actions-bar">
            <button type="button" className="dmp-btn" onClick={assign} disabled={!picked || saving}>
              {saving ? "Assigning…" : picked ? `Assign ${picked} to this client` : "Assign to this client"}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

/** Webhook URL + key to paste into each Google Ads lead form. */
export function GoogleLeadFormsCard({ api, tenantId }) {
  const notify = useNotify();
  const confirm = useConfirm();
  const [status, setStatus] = useState(null);
  const [freshKey, setFreshKey] = useState(null);
  const params = api.isStaff ? { tenant_id: tenantId } : undefined;

  useEffect(() => {
    api
      .googleLeadKey(params)
      .then(setStatus)
      .catch(() => setStatus(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, tenantId]);

  const generate = async () => {
    if (status?.has_key && !(await confirm({
      title: "New webhook key",
      message: "Lead forms using the old key will stop delivering until you paste the new key into them.",
    }))) {
      return;
    }
    try {
      const res = await api.createGoogleLeadKey(params || {});
      setFreshKey(res.key);
      setStatus((s) => ({ ...(s || {}), has_key: true, key_hint: res.key_hint, url: res.url || s?.url }));
    } catch (err) {
      notify.error(errorMessage(err, "Could not create a key"));
    }
  };

  return (
    <div className="dmp-card">
      <h3 className="dmp-card__title">Google lead forms</h3>
      <p className="wk-muted" style={{ marginTop: -6 }}>
        In Google Ads, open each lead form &gt; Lead delivery &gt; Webhook, paste this URL and key, and press "Send test
        data". New leads then land in Website Leads with an email alert.
      </p>
      <CopyField label="Webhook URL" value={status?.url} />
      {freshKey ? (
        <>
          <CopyField label="Key (shown only now)" value={freshKey} />
          <p className="wk-muted" style={{ fontSize: 12 }}>
            Copy it now. Cloud Office keeps only a fingerprint and cannot show it again.
          </p>
        </>
      ) : status?.vault_key ? (
        <p className="wk-muted">Using the Lead Form Webhook Key from your Vault (Google Ads app).</p>
      ) : status?.has_key ? (
        <p className="wk-muted">A key is set (ends {status.key_hint}).</p>
      ) : null}
      <button type="button" className="dmp-btn dmp-btn--ghost" onClick={generate}>
        <FaKey aria-hidden="true" /> {status?.has_key ? "Create a new key" : "Create webhook key"}
      </button>
    </div>
  );
}
